/* eslint-disable no-useless-computed-key */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Box, MenuItem, TextField, withStyles } from '@material-ui/core';
import { Button, Dialog, DialogActions, Typography } from '@mui/material';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { setPageTitle, showToast } from 'modules/layout/layout.actions';
import { TOAST_MESSAGE_SEVERITY_ERROR } from 'modules/layout/types';
import { createVoucher, getVoucherSummaries, getAllPreferredExpenseCodes } from 'modules/expenses/store/expenses.actions';
import {
  GET_CLIENT_PREFERRED_EXPENSE_CODES_FAILURE,
  GET_VOUCHER_SUMMARIES_FAILURE,
  POST_VOUCHER_SUMMARY_FAILURE
} from 'modules/expenses/store/types';
import { getAuthorizationSummaries } from 'modules/authorizations/store/actions/authorizations.actions';
import { GET_AUTHORIZATIONS_FAILURE } from 'modules/authorizations/store/types/authorizationTypes';
import { getVendors } from 'modules/authorizations/store/actions/dataManagement.actions';
import { GET_VENDORS_FAILURE } from 'modules/authorizations/store/types/dataManagementTypes';

import VouchersGrid from './vouchersGrid.component';
import FullscreenSpinner from 'modules/common/fullscreenSpinner.component';
import AdvancedSearch from 'modules/common/advancedSearch.component';
import Spinner from 'modules/common/spinner.component';

import {
  getAllCurrencies,
  getAllEnumerations,
  getAllProvinces,
  getAllSaleTypes,
  getAllWeightTypes
} from 'modules/enumerations/store/enumerationActions';
import { getAllCountries } from 'country-locale-map';
import { buildCreateVoucherRequest, norm } from './voucherConstants';

import { getClients } from 'modules/clients/clients.actions';
import { GET_CLIENTS_FAILURE } from 'modules/clients/types';
import { Permissions } from 'modules/layout/permissions';
import { hasPermission } from 'modules/layout/permissionService';

const styles = theme => ({
  headerContainer: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'flex-start',
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1)
  },
  textField: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    minWidth: 100
  }
});

const VoucherSummariesView = props => {
  const {
    classes,
    history,
    voucherSummaries,
    clients,
    vendors,
    authorizationSummaries,
    setPageTitle,
    showToast,
    createVoucher,
    getAuthorizationSummaries,
    getVendors,
    getVoucherSummaries,
    location,
    enumerations,
    getAllEnumerations,
    clientPreferredExpenseCodes,
    getClients
  } = props;

  const STATUS_LABELS = ['Draft', 'Pending', 'On Hold', 'Approved', 'Denied'];
  const canEditVouchers = hasPermission(Permissions.Finance.Vouchers.Edit);

  const allEnums = useMemo(() => enumerations?.allEnums ?? {}, [enumerations]);
  const provinces = useMemo(() => (Array.isArray(allEnums?.provinces) ? allEnums.provinces.filter(p => !!p?.isActive) : []), [allEnums]);
  const provinceTitleById = useMemo(() => {
    const map = {};
    for (const p of provinces) map[p.id] = p.title ?? '';
    return map;
  }, [provinces]);

  const clientsArray = useMemo(() => (Array.isArray(clients) ? clients : Object.values(clients ?? {})), [clients]);

  const normalizedClients = useMemo(
    () =>
      (clientsArray ?? []).map(c => ({
        clientRecordId: c.clientRecordId ?? c.id ?? c.clientId,
        legalName: c.legalName ?? c.clientName ?? c.description ?? '',
        id: c.clientRecordId ?? c.id ?? c.clientId,
        description: c.legalName ?? c.clientName ?? c.description ?? '',
        clientId: c.clientRecordId ?? c.id ?? c.clientId,
        clientName: c.shortName ?? c.legalName ?? c.clientName ?? c.description ?? ''
      })),
    [clientsArray]
  );

  const normalizedVendors = useMemo(() => {
    const arr = Array.isArray(vendors) ? vendors : Object.values(vendors ?? {});
    return arr.map(v => {
      const stateId = v.stateId ?? v.provinceId ?? v.state ?? v.stateGuid ?? null;
      return { ...v, stateId, stateTitle: stateId ? provinceTitleById[stateId] ?? '' : '' };
    });
  }, [vendors, provinceTitleById]);

  const normalizedAuthorizations = useMemo(() => {
    const full = a =>
      `${(a.transfereeFirstName ?? a.firstName ?? '').trim()} ${(a.transfereeLastName ?? a.lastName ?? '').trim()}`
        .trim()
        .replace(/\s+/g, ' ');
    return (authorizationSummaries ?? []).map(a => ({
      ...a,
      fileId: a.fileId ?? a.authorizationId ?? a.authId ?? null,
      transfereeName: a.transfereeName?.trim() ? a.transfereeName : full(a),
      clientName: a.clientName ?? '',
      departureAddress: a.departureAddress ?? a.departure ?? '',
      destinationAddress: a.destinationAddress ?? a.destination ?? ''
    }));
  }, [authorizationSummaries]);

  const sortedClients = useMemo(
    () => normalizedClients.filter(c => !!c.clientName).sort((a, b) => String(a.clientName).localeCompare(String(b.clientName))),
    [normalizedClients]
  );

  const clientAdvInfo = useMemo(
    () => ({
      searchingColumns: [
        { name: 'clientId', title: 'Client Id' },
        { name: 'clientName', title: 'Company Name' }
      ],
      searchingColumnExtensions: [
        { columnName: 'clientId', width: 150 },
        { columnName: 'clientName', width: 250 }
      ],
      searchingRows: normalizedClients,
      idKey: 'clientId',
      nameKey: 'clientName'
    }),
    [normalizedClients]
  );

  const [clientId, setClientId] = useState(-1);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [showCreateVoucher, setShowCreateVoucher] = useState(false);
  const [payToVendor, setPayToVendor] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [selectedClientName, setSelectedClientName] = useState(null);
  const [selectedVendorId, setSelectedVendorId] = useState(null);
  const [selectedVendorName, setSelectedVendorName] = useState(null);
  const [selectedAuthId, setSelectedAuthId] = useState(null);
  const [selectedAuthName, setSelectedAuthName] = useState(null);
  const [isBootLoading, setIsLoadingSummaries] = useState(false);
  const [isLoadingModal, setIsLoadingModal] = useState(false);

  useEffect(() => {
    setPageTitle('Vouchers');
    try {
      const qp = new URLSearchParams(location?.search || '');
      const statusParam = qp.get('status');
      if (statusParam) {
        const match = STATUS_LABELS.find(l => norm(l) === norm(statusParam));
        if (match) setSelectedStatuses([match]);
      }
    } catch { }
    (async () => {
      setIsLoadingSummaries(true);
      if (!voucherSummaries) {
        const { type } = await getVoucherSummaries();
        if (type === GET_VOUCHER_SUMMARIES_FAILURE)
          showToast('Failed to retrieve vouchers, please try again', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      }
      if (!vendors) {
        const { type } = await getVendors();
        if (type === GET_VENDORS_FAILURE)
          showToast('Failed to retrieve vendors, please try again', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      }
      if (!clientPreferredExpenseCodes) {
        const { type } = await getAllPreferredExpenseCodes();
        if (type === GET_CLIENT_PREFERRED_EXPENSE_CODES_FAILURE)
          showToast('Failed to retrieve client preferred expense codes, please try again', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      }
      if (!authorizationSummaries) {
        const { type } = await getAuthorizationSummaries();
        if (type === GET_AUTHORIZATIONS_FAILURE)
          showToast('Failed to retrieve authorizations, please try again', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      }
      if (!clients) {
        const { type } = await getClients();
        if (type === GET_CLIENTS_FAILURE) {
          showToast('Failed to retrieve data, please try again', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
        }
      }
      if (!enumerations?.allEnums) await getAllEnumerations();
      setIsLoadingSummaries(false);
    })();
  }, []);

  const changeStatusFilters = useCallback(
    label => setSelectedStatuses(prev => (prev.includes(label) ? prev.filter(s => s !== label) : [...prev, label])),
    []
  );

  const togglePayToVendor = useCallback(() => setPayToVendor(prev => !prev), []);

  const onVoucherCreationCancelled = () => {
    setClientId(-1);
    setPayToVendor(false);
    setSelectedClientId(null);
    setSelectedClientName(null);
    setSelectedVendorId(null);
    setSelectedVendorName(null);
    setSelectedAuthId(null);
    setSelectedAuthName(null);
    setShowCreateVoucher(false);
  }

  const createVoucherRequest = useCallback(async () => {
    if (!canEditVouchers) return;
    setIsLoadingModal(true);
    const hasClient = selectedClientId != null;
    const hasPayee = payToVendor ? !!selectedVendorId : !!selectedAuthId;
    if (!hasClient || !hasPayee) {
      showToast('Please select a client and a valid payee (Vendor or Authorization).', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      setIsLoadingModal(false);
      return;
    }
    const request = buildCreateVoucherRequest({
      clientId: selectedClientId,
      payToVendor,
      vendorId: selectedVendorId,
      authorizationId: selectedAuthId
    });
    const resp = await createVoucher(request);
    if (!resp || resp.type === POST_VOUCHER_SUMMARY_FAILURE) {
      showToast('Failed to create new voucher, please try again', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      setIsLoadingModal(false);
      return;
    }
    const newId = resp?.response?.voucherInformation?.voucherId;
    if (!newId) {
      showToast('Voucher created but no ID returned.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      setIsLoadingModal(false);
      return;
    }
    history.push(`/vouchers/${newId}`);
  }, [canEditVouchers, createVoucher, payToVendor, selectedClientId, selectedVendorId, selectedAuthId, showToast, history]);

  const selectedClientNameForFilter = useMemo(() => {
    if (clientId === -1) return null;
    return normalizedClients.find(c => c.clientId === clientId)?.clientName ?? null;
  }, [clientId, normalizedClients]);

  const filteredVoucherSummaries = useMemo(() => {
    const list = voucherSummaries || [];
    return list.filter(s => {
      const statusOk = (selectedStatuses?.length ?? 0) === 0 || selectedStatuses.map(norm).includes(norm(s.status));
      const clientOk = !selectedClientNameForFilter || norm(s.clientName) === norm(selectedClientNameForFilter);
      return statusOk && clientOk;
    });
  }, [voucherSummaries, selectedStatuses, selectedClientNameForFilter]);

  if (isBootLoading) return <FullscreenSpinner />;

  return (
    <Fragment>
      <div className={classes.headerContainer}>
        <TextField
          select
          label="Clients"
          className={classes.textField}
          value={clientId}
          onChange={e => setClientId(Number(e.target.value))}
          SelectProps={{ autoWidth: true }}
          variant="outlined"
        >
          <MenuItem value={-1}>
            <em>All Clients</em>
          </MenuItem>
          {sortedClients.map(option => (
            <MenuItem key={option.clientId} value={option.clientId}>
              {option.clientName}
            </MenuItem>
          ))}
        </TextField>
      </div>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: '10px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', py: '8px' }}>
          <Typography>Voucher Status:</Typography>
          {STATUS_LABELS.map(label => {
            const active = selectedStatuses.includes(label);
            return (
              <Button
                key={label}
                color="secondary"
                variant="contained"
                sx={{
                  maxHeight: '40px',
                  backgroundColor: active ? '#36939b' : '#ffffff',
                  '&:hover': { backgroundColor: active ? '#15585e' : '#faf8f2' },
                  color: active ? '#ffffff' : '#080600'
                }}
                onClick={() => changeStatusFilters(label)}
              >
                {label}
              </Button>
            );
          })}
        </Box>

        <Button
          color="secondary"
          variant="contained"
          sx={{
            maxHeight: '40px',
            backgroundColor: '#5D9878',
            '&:hover': { backgroundColor: '#528569' }
          }}
          onClick={() => setShowCreateVoucher(true)}
          disabled={!canEditVouchers}
        >
          Create Voucher
        </Button>
      </Box>

      {isBootLoading && <FullscreenSpinner />}
      {!isBootLoading && (
        <div className="px-2">
          <VouchersGrid vouchers={filteredVoucherSummaries} history={history} />
        </div>
      )}

      <Dialog open={showCreateVoucher} fullWidth={false} maxWidth="lg">
        <Box sx={{ p: '25px' }}>
          <Typography variant="h5">New Voucher</Typography>
        </Box>
        <Box sx={{ px: '25px', pt: 0 }}>
          <Typography variant="body2">Select from the base options below to get started with voucher creation.</Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'row', p: '10px 20px' }}>
          <Box sx={{ mr: '20px' }}>
            <AdvancedSearch
              labelText="Client"
              displayValueAccessorKey="clientName"
              saveIdValueAccessorKey="clientId"
              searchingCriteria={clientAdvInfo.searchingColumns}
              searchingCriteriaExtensions={clientAdvInfo.searchingColumnExtensions}
              searchingRows={normalizedClients}
              id={selectedClientId}
              value={selectedClientName}
              setIdValue={(id, value) => {
                setSelectedAuthId(null);
                setSelectedAuthName(null);
                setSelectedClientId(id);
                setSelectedClientName(value);
              }}
              isReadOnly={false}
            />
            <Typography variant="caption" display="block" color="primary">
              Required
            </Typography>
          </Box>

          <Box>
            <Button
              color="secondary"
              variant="contained"
              sx={{
                maxHeight: '40px',
                ml: '12px',
                mt: '8px',
                backgroundColor: payToVendor ? '#36939b' : '#ffffff',
                '&:hover': { backgroundColor: payToVendor ? '#15585e' : '#faf8f2' },
                color: payToVendor ? '#ffffff' : '#080600'
              }}
              onClick={togglePayToVendor}
            >
              Pay to Vendor
            </Button>
            <Button
              color="secondary"
              variant="contained"
              sx={{
                maxHeight: '40px',
                ml: '12px',
                mt: '8px',
                backgroundColor: !payToVendor ? '#36939b' : '#ffffff',
                '&:hover': { backgroundColor: !payToVendor ? '#15585e' : '#faf8f2' },
                color: !payToVendor ? '#ffffff' : '#080600'
              }}
              onClick={togglePayToVendor}
            >
              Pay to EE
            </Button>
          </Box>

          {payToVendor ? (
            <Box sx={{ ml: '25px' }}>
              <AdvancedSearch
                labelText="Vendor"
                displayValueAccessorKey="companyName"
                saveIdValueAccessorKey="vendorId"
                searchingCriteria={[
                  { name: 'vendorId', title: 'Vendor Id' },
                  { name: 'companyName', title: 'Company Name' },
                  { name: 'city', title: 'City' },
                  { name: 'stateTitle', title: 'State' },
                  { name: 'phone', title: 'Company Phone' },
                  { name: 'email', title: 'Company Email' }
                ]}
                searchingCriteriaExtensions={[
                  { columnName: 'vendorId', width: 150 },
                  { columnName: 'companyName', width: 250 },
                  { columnName: 'city', width: 175 },
                  { columnName: 'stateTitle', width: 140 },
                  { columnName: 'phone', width: 175 },
                  { columnName: 'email', width: 175 }
                ]}
                searchingRows={normalizedVendors}
                id={selectedVendorId}
                value={selectedVendorName}
                setIdValue={(id, value) => {
                  setSelectedVendorId(id);
                  setSelectedVendorName(value);
                }}
                isReadOnly={false}
              />
              <Typography variant="caption" display="block" color="primary">
                Required
              </Typography>
            </Box>
          ) : (
            <Box sx={{ ml: '25px' }}>
              <AdvancedSearch
                labelText="Authorization"
                displayValueAccessorKey="transfereeName"
                saveIdValueAccessorKey="fileId"
                searchingCriteria={[
                  { name: 'fileNumber', title: 'Auth Id' },
                  { name: 'transfereeName', title: 'Full Name' },
                  { name: 'departureAddress', title: 'Departure Location' },
                  { name: 'destinationAddress', title: 'Destination Location' },
                  { name: 'clientName', title: 'Client' }
                ]}
                searchingCriteriaExtensions={[
                  { columnName: 'fileNumber', width: 150 },
                  { columnName: 'transfereeName', width: 250 },
                  { columnName: 'departureAddress', width: 175 },
                  { columnName: 'destinationAddress', width: 175 },
                  { columnName: 'clientName', width: 175 }
                ]}
                searchingRows={
                  selectedClientName
                    ? normalizedAuthorizations.filter(a => a.clientRecordId === selectedClientId)
                    : normalizedAuthorizations
                }
                id={selectedAuthId}
                value={selectedAuthName}
                setIdValue={(id, value) => {
                  const auth = normalizedAuthorizations.find(a => a.fileId === id);
                  setSelectedAuthId(id);
                  setSelectedAuthName(value);
                  if (auth?.clientRecordId) {
                    const c = normalizedClients.find(x => x.clientId === auth.clientRecordId);
                    if (c) {
                      setSelectedClientId(c.clientId);
                      setSelectedClientName(c.clientName);
                    }
                  }
                }}
                isReadOnly={false}
              />
              <Typography variant="caption" display="block" color="primary">
                Required
              </Typography>
            </Box>
          )}
        </Box>

        <DialogActions sx={{ p: '1.25rem' }}>
          {isLoadingModal && <Spinner />}
          {!isLoadingModal && (
            <>
              <Button onClick={onVoucherCreationCancelled} sx={{ color: '#41B7C0', '&:hover': { color: '#3d959c' } }}>
                Cancel
              </Button>
              <Button
                onClick={createVoucherRequest}
                color="primary"
                sx={{ backgroundColor: '#41B7C0', '&:hover': { backgroundColor: '#3d959c' } }}
                type="submit"
                variant="contained"
                disabled={
                  !canEditVouchers ||
                  selectedClientId == null ||
                  (payToVendor && (!selectedVendorId || selectedVendorId === '')) ||
                  (!payToVendor && (selectedAuthId == null || selectedAuthId === ''))
                }
              >
                Create Voucher
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

const mapStateToProps = state => {
  const {
    authorizations: { authorizationSummaries, vendors, isLoading },
    expenses: { voucherSummaries, clientPreferredExpenseCodes },
    clients: { clients }
  } = state;

  return {
    voucherSummaries,
    clients,
    isLoading,
    vendors,
    authorizationSummaries,
    enumerations: state.enumerations,
    clientPreferredExpenseCodes
  };
};

export default compose(
  withStyles(styles),
  withRouter,
  connect(mapStateToProps, {
    setPageTitle,
    showToast,
    getVoucherSummaries,
    getAuthorizationSummaries,
    getVendors,
    createVoucher,
    getAllEnumerations,
    getAllCountries,
    getAllCurrencies,
    getAllProvinces,
    getAllSaleTypes,
    getAllWeightTypes,
    getAllPreferredExpenseCodes,
    getClients
  })
)(VoucherSummariesView);
